import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { APIStatus } from '@application/enums/api-status';
import { getSuggestedPeopleForPartnerAsync } from '@application/features/partner/partnerThunk';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Avatar, Box, Button, CircularProgress, Typography, useMediaQuery, useTheme, IconButton, Tooltip, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StyledSearchInput from '@ui/components/StyledSearchInput';
import Grid from '@mui/material/Grid2';
import { FullHeightCard } from '@ui/pages/Home/components';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SuggestionCards from '../../InterestTab/components/SuggestionCard';
import NoDataMessage from '@ui/components/common/NoDataMessage';
import { useTranslation } from 'react-i18next';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { between } from '@ui/style/style';
import { Upload, Paper, Download } from 'react-iconly';
import AssignDialog from './AssignDialog';
import ImportTicketDialog from './ImportTicketDialog';
import AssignTicketFromSuggestionDialog from './AssignTicketFromSuggestionDialog';
import { toast } from 'react-toastify';
import { getAllTicketsAsync, sendTicketEmailAsync, sendBulkTicketEmailAsync, UnassignTicketsAsync } from '@application/features/tickets/ticketsThunk';
import { useSelector } from 'react-redux';
import { Copy } from 'iconsax-react';
const Tickets = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { id: partnerId, eventId } = useParams();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useAppDispatch();
    const { suggestedPeople, status } = useAppSelector((state) => state.partner);
    const [openImportExcelDialog, setOpenImportExcelDialog] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [selectedTicketIds, setSelectedTicketIds] = useState([]);
    const { tickets, ticket } = useSelector((state) => state.tickets);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    // Infinite scroll state for suggestions
    const [currentSkip, setCurrentSkip] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const scrollTimerRef = useRef(null);
    const suggestionsContainerRef = useRef(null);
    const ITEMS_PER_PAGE = 10;
    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // 500ms delay
        return () => clearTimeout(timer);
    }, [searchTerm]);
    // Fetch suggested people when search term changes
    useEffect(() => {
        if (partnerId) {
            // Reset pagination when search changes
            setCurrentSkip(0);
            setIsAtBottom(false);
            dispatch(getSuggestedPeopleForPartnerAsync({
                partnerId,
                eventId,
                search: debouncedSearchTerm || undefined,
                skip: 0,
                take: ITEMS_PER_PAGE,
                includePeople: true,
                includeExternalRelations: true
            }));
        }
    }, [dispatch, partnerId, eventId, debouncedSearchTerm]);
    useEffect(() => {
        getAllTickets();
    }, [dispatch, eventId, ticket, paginationModel]);
    const getAllTickets = () => {
        if (eventId) {
            const refreshRequest = {
                eventId: eventId,
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: null
            };
            dispatch(getAllTicketsAsync(refreshRequest));
        }
    };
    //  map suggested people
    const suggestions = suggestedPeople?.items.map((person) => ({
        id: person.personId,
        name: person.name,
        title: person.title || '',
        email: person.email || '',
        description: person.previousActivity?.description || '',
        avatar: person.profilePictureUrl || '',
        tags: Object.entries(person.interestBreakdown).map(([label, rating]) => ({
            label,
            rating
        }))
    })) || [];
    const handleSuggestionAction = (suggestionId, action) => {
        if (action === 'assign') {
            const suggestion = suggestedPeople?.items.find((person) => person.personId === suggestionId);
            if (suggestion) {
                setSelectedSuggestion(suggestion);
                setAssignTicketFromSuggestionDialogOpen(true);
            }
        }
    };
    const isLoading = status === APIStatus.PENDING;
    const hasMoreSuggestions = suggestedPeople?.hasMore || false;
    const isInitialSuggestionsLoading = isLoading && currentSkip === 0 && (suggestedPeople?.items?.length ?? 0) === 0;
    // Load more suggestions function - makes API call
    const loadMoreSuggestions = async () => {
        if (isLoadingMore || !hasMoreSuggestions || !partnerId)
            return;
        setIsLoadingMore(true);
        const nextSkip = currentSkip + ITEMS_PER_PAGE;
        try {
            await dispatch(getSuggestedPeopleForPartnerAsync({
                partnerId,
                eventId,
                search: debouncedSearchTerm || undefined,
                skip: nextSkip,
                take: ITEMS_PER_PAGE,
                includePeople: true,
                includeExternalRelations: true
            })).unwrap();
            // Update current skip if successful
            setCurrentSkip(nextSkip);
            // Hide fallback button after new items load; user must scroll to bottom again
            setIsAtBottom(false);
        }
        catch (error) {
            console.error('Error loading more suggestions:', error);
        }
        finally {
            setIsLoadingMore(false);
        }
    };
    // Handle scroll for suggestions
    const handleSuggestionsScroll = (event) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        // Calculate how far we are from the bottom
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        // Check if user is at the bottom (within 50px threshold)
        const atBottom = distanceFromBottom <= 50;
        setIsAtBottom(atBottom);
        // Clear any existing timer to properly debounce
        if (scrollTimerRef.current) {
            clearTimeout(scrollTimerRef.current);
            scrollTimerRef.current = null;
        }
        // If scrolled to near the bottom and not loading and has more content
        if (distanceFromBottom < 150 && !isLoadingMore && hasMoreSuggestions) {
            // Set a new debounced timer
            scrollTimerRef.current = setTimeout(() => {
                loadMoreSuggestions();
            }, 200);
        }
    };
    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (scrollTimerRef.current) {
                clearTimeout(scrollTimerRef.current);
            }
        };
    }, []);
    const getInitials = (name) => {
        if (!name)
            return '';
        return name
            .split(' ')
            .filter(Boolean)
            .map((n) => n[0].toUpperCase())
            .slice(0, 2)
            .join('');
    };
    const mapTicketsToRows = (tickets) => (tickets || []).map((ticket) => ({
        id: ticket.id,
        name: `${ticket.ticketNo}`,
        initials: '',
        link: ticket.ticketUrl,
        ticketType: ticket.ticketType,
        venueSection: ticket.venueSection,
        venueSectionRow: ticket.venueSectionRow,
        venueSectionSeat: ticket.venueSectionSeat,
        barcode: ticket.barcode,
        assignedTo: ticket.assignedTo
            ? {
                name: ticket.assignedTo,
                initials: getInitials(ticket.assignedTo),
                email: ticket.assignedToEmail || ''
            }
            : null,
        isTicketEmailSent: ticket.isTicketEmailSent
    }));
    // initial rows
    const initialRows = [
        {
            id: '128749912447',
            name: 'Ticket 128749912447',
            initials: 'FB',
            link: 'https://example.com/tickets/128749912447',
            assignedTo: { name: 'Fate Basit', initials: 'FB' }
        },
        {
            id: '128749912448',
            name: 'Ticket 128749912448',
            initials: '',
            link: 'https://example.com/tickets/128749912448',
            assignedTo: null
        }
    ];
    // load persisted rows if available
    const ticketRows = useMemo(() => {
        const ticketsList = tickets?.items || tickets?.tickets || [];
        return mapTicketsToRows(ticketsList);
    }, [tickets]);
    // Determine which tickets are selectable (assigned and email not sent)
    const isTicketSelectable = useCallback((row) => {
        return row.assignedTo !== null && !row.isTicketEmailSent;
    }, []);
    // Get selectable ticket IDs from current page
    const selectableTicketIds = useMemo(() => {
        return ticketRows.filter(isTicketSelectable).map((row) => row.id);
    }, [ticketRows, isTicketSelectable]);
    // Clear selections for tickets that are no longer selectable
    useEffect(() => {
        setSelectedTicketIds((prev) => prev.filter((id) => {
            const row = ticketRows.find((r) => r.id === id);
            return row ? isTicketSelectable(row) : false;
        }));
    }, [ticketRows, isTicketSelectable]);
    // Handle selection change - only allow selecting eligible tickets
    const handleSelectionChange = useCallback((newSelection) => {
        // Filter to only include selectable tickets
        const validSelection = newSelection.filter((id) => selectableTicketIds.includes(id));
        setSelectedTicketIds(validSelection);
    }, [selectableTicketIds]);
    // Handle sending emails to all selected tickets
    const handleSendEmailsToSelected = useCallback(() => {
        if (selectedTicketIds.length === 0) {
            toast.warning(t('sponsorships.tickets.noTicketsSelected') || 'Please select at least one ticket');
            return;
        }
        const request = {
            ticketIds: selectedTicketIds
        };
        dispatch(sendBulkTicketEmailAsync(request))
            .unwrap()
            .then((response) => {
            // Refresh tickets after sending
            getAllTickets();
            // Show success/error message
            if (response.isSuccess) {
                toast.success(response.message ||
                    t('sponsorships.tickets.sendEmailsSuccess', { count: selectedTicketIds.length }) ||
                    `Successfully sent ${selectedTicketIds.length} email${selectedTicketIds.length > 1 ? 's' : ''}`);
                // Clear selection after sending
                setSelectedTicketIds([]);
            }
            else {
                toast.error(response.message ||
                    t('sponsorships.tickets.sendEmailsError') ||
                    'An error occurred while sending emails');
            }
        });
    }, [selectedTicketIds, dispatch, eventId, paginationModel, t]);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuRowId, setMenuRowId] = useState(null);
    const [menuType, setMenuType] = useState(null);
    const openMenu = (event, rowId, type) => {
        event.stopPropagation(); // Prevent row selection
        setMenuAnchor(event.currentTarget);
        setMenuRowId(rowId);
        setMenuType(type);
    };
    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuRowId(null);
        setMenuType(null);
    };
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [assignDialogType, setAssignDialogType] = useState(null);
    const [assignDialogRowId, setAssignDialogRowId] = useState(null);
    const [assignTicketFromSuggestionDialogOpen, setAssignTicketFromSuggestionDialogOpen] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const handleAssignOption = (rowId, option) => {
        // open the AssignDialog and pass the selected type
        setAssignDialogRowId(rowId);
        setAssignDialogType(option);
        setAssignDialogOpen(true);
        closeMenu();
    };
    const handleAssignDialogClose = () => {
        setAssignDialogOpen(false);
        setAssignDialogType(null);
        setAssignDialogRowId(null);
    };
    const handleAssignConfirm = (values) => {
        const fullName = [values.firstName, values.middleName, values.lastName]
            .filter(Boolean)
            .join(' ');
        const initials = ((values.firstName?.[0] || '') + (values.lastName?.[0] || '')).toUpperCase().slice(0, 2) ||
            'NA';
        toast.success(t('sponsorships.tickets.assignSuccess') || 'Ticket assigned');
        handleAssignDialogClose();
    };
    const handleSendOption = (rowId, option) => {
        if (option === 'SendEmail') {
            if (rowId) {
                var request = {
                    ticketId: rowId
                };
                dispatch(sendTicketEmailAsync(request))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || 'The Ticket has been sent successfully.');
                    }
                    else {
                        toast.error(response.message || 'The Email was not sent.');
                    }
                });
            }
        }
        else if (option === 'Unassign') {
            if (rowId) {
                dispatch(UnassignTicketsAsync(rowId))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || 'The Ticket has been unassigned.');
                        if (partnerId) {
                            // Reset pagination when refreshing
                            setCurrentSkip(0);
                            setIsAtBottom(false);
                            dispatch(getSuggestedPeopleForPartnerAsync({
                                partnerId: partnerId,
                                eventId: eventId,
                                search: debouncedSearchTerm || undefined,
                                skip: 0,
                                take: ITEMS_PER_PAGE,
                                includePeople: true,
                                includeExternalRelations: true
                            }));
                        }
                    }
                    else {
                        toast.error(response.message || 'The ticket was not unassigned successfully.');
                    }
                });
            }
        }
        closeMenu();
    };
    const currentMenuRow = useMemo(() => ticketRows.find((row) => row.id === menuRowId) || null, [menuRowId, ticketRows]);
    // columns here
    const ticketsColumns = [
        {
            field: 'name',
            headerName: t('sponsorships.tickets.columnName'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsx(Box, { display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0, children: _jsx(Box, { flex: 1, minWidth: 0, children: _jsx(Typography, { variant: 'body2', color: 'text.primary', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', children: params.row.name || '-' }) }) }))
        },
        {
            field: 'ticketType',
            headerName: t('sponsorships.tickets.ticketType'),
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            minWidth: 90,
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: params.row.ticketType || '-' }))
        },
        {
            field: 'venueSection',
            headerName: t('sponsorships.tickets.ticketStand'),
            flex: 1,
            minWidth: 90,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: params.row.venueSection || '-' }))
        },
        {
            field: 'venueSectionRow',
            headerName: t('sponsorships.tickets.ticketRow'),
            flex: 1,
            minWidth: 90,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: params.row.venueSectionRow || '-' }))
        },
        {
            field: 'venueSectionSeat',
            headerName: t('sponsorships.tickets.ticketSeatNumber'),
            flex: 1,
            minWidth: 120,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: params.row.venueSectionSeat || '-' }))
        },
        {
            field: 'link',
            headerName: t('sponsorships.tickets.ticketLink'),
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0, children: [_jsx(Tooltip, { title: t('common.copy', { defaultValue: 'Copy' }), children: _jsx(IconButton, { size: 'small', "aria-label": t('common.copy', { defaultValue: 'Copy' }), sx: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '8px',
                                width: 36,
                                height: 36,
                                '& svg': {
                                    width: 18,
                                    height: 18
                                }
                            }, onClick: async (e) => {
                                e.stopPropagation(); // Prevent row selection
                                const text = params.row?.link || '';
                                try {
                                    if (navigator.clipboard && navigator.clipboard.writeText) {
                                        await navigator.clipboard.writeText(text);
                                        toast.success(t('sponsorships.tickets.linkCopied') || 'Link copied to clipboard');
                                    }
                                    else {
                                        const textarea = document.createElement('textarea');
                                        textarea.value = text;
                                        document.body.appendChild(textarea);
                                        textarea.select();
                                        document.execCommand('copy');
                                        textarea.remove();
                                        toast.success(t('sponsorships.tickets.linkCopied') || 'Link copied to clipboard');
                                    }
                                }
                                catch (err) {
                                    console.error('Copy failed', err);
                                    toast.error(t('sponsorships.tickets.copyFailed') || 'Unable to copy link');
                                }
                            }, children: _jsx(Copy, { size: 18 }) }) }), _jsx(Tooltip, { title: t('common.download', { defaultValue: 'Download' }), children: _jsx(IconButton, { size: 'small', "aria-label": t('common.download', { defaultValue: 'Download' }), component: 'a', href: params.row?.link || params.value, target: '_blank', rel: 'noopener noreferrer', download: true, sx: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '8px',
                                width: 36,
                                height: 36,
                                '& svg': {
                                    width: 18,
                                    height: 18
                                }
                            }, children: _jsx(Download, { set: 'light', size: 18 }) }) })] }))
        },
        {
            field: 'assignedTo',
            headerName: t('sponsorships.tickets.columnAssignedTo'),
            flex: 1,
            minWidth: 250,
            renderCell: (params) => {
                const assigned = params.row?.assignedTo;
                if (assigned) {
                    return (_jsxs(Box, { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', children: [_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, children: [_jsx(Avatar, { sx: { width: 32, height: 32, fontSize: '12px' }, children: assigned.initials }), _jsxs(Box, { minWidth: 0, children: [_jsx(Typography, { variant: 'body2', color: 'text.primary', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', children: assigned.name }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', children: assigned.email })] })] }), _jsx(Box, { pl: 0.2, children: _jsx(Button, { size: 'small', variant: 'outlined', color: 'success', endIcon: params.row.isTicketEmailSent ? undefined : _jsx(KeyboardArrowDownIcon, {}), sx: { textTransform: 'none', minWidth: 84 }, onClick: (e) => !params.row.isTicketEmailSent && openMenu(e, params.row.id, 'send'), disabled: params.row.isTicketEmailSent, children: params.row.isTicketEmailSent
                                        ? t('sponsorships.tickets.sent', { defaultValue: 'Sent' })
                                        : t('sponsorships.tickets.send', { defaultValue: 'Send' }) }) })] }));
                }
                return (_jsx(Box, { display: 'flex', alignItems: 'center', width: '100%', children: _jsx(Button, { size: 'small', variant: 'soft', color: 'primary', endIcon: _jsx(KeyboardArrowDownIcon, {}), sx: { textTransform: 'none', minWidth: 84 }, onClick: (e) => openMenu(e, params.row.id, 'assign'), children: t('sponsorships.tickets.assignLabel', { defaultValue: 'Assign' }) }) }));
            }
        }
    ];
    return (_jsxs(Box, { children: [_jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: { xs: 12, sm: 8 }, sx: {
                            pb: isSmallScreen ? 0 : 1
                        }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '12px', children: [_jsxs(Box, { sx: { ...between }, children: [_jsx(Typography, { variant: 'h4', color: 'text.primary', children: t('sponsorships.tickets.title') }), _jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [selectedTicketIds.length > 0 && (_jsx(Button, { startIcon: _jsx(Paper, { set: 'curved' }), size: isMobile ? 'small' : 'large', variant: 'contained', color: 'primary', onClick: handleSendEmailsToSelected, children: t('sponsorships.tickets.sendEmailsToSelected', {
                                                        count: selectedTicketIds.length
                                                    }) ||
                                                        `Send Email${selectedTicketIds.length > 1 ? 's' : ''} (${selectedTicketIds.length})` })), _jsx(Button, { startIcon: _jsx(Upload, {}), size: isMobile ? 'small' : 'large', variant: 'outlined', color: 'primary', 
                                                    //onClick={() => setOpenImportPdfDialog(true)}
                                                    onClick: () => setOpenImportExcelDialog(true), children: t('sponsorships.tickets.uploadLabel') })] })] }), _jsx(PrimaryGrid, { rows: ticketRows, columns: ticketsColumns, maxHeight: 'calc(100dvh - 250px)', sx: { height: 'calc(100dvh - 250px)' }, showFilters: false, showHeaderControls: false, showProgress: true, progressPercent: tickets?.usedPercentage, paginationMode: 'server', paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, rowCount: tickets?.totalCount || 0, pageSizeOptions: [5, 10, 25, 50], isMultiple: true, rowSelectionModel: selectedTicketIds, onSelectionChange: handleSelectionChange, isRowSelectable: (params) => isTicketSelectable(params.row) })] }) }), _jsx(Grid, { size: { xs: 12, sm: 4 }, children: _jsx(Box, { sx: { height: 'calc(100vh - 140px)' }, children: _jsx(FullHeightCard, { filter: false, heading: t('sponsorships.interestsTab.suggestions') || 'Suggestions', headerTypographyColor: 'text.primary', headerTypographyVariant: 'h4', cardContentContainersx: { padding: 0 }, cardHeaderPadding: '1px 6px 12px 4px', disableBorders: true, disableShadow: true, disableBackground: true, height: 'calc(100dvh - 184px)', actionContent: _jsx(StyledSearchInput, { sx: { width: 194, mt: 0.5 }, value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), handleResetCallback: () => setSearchTerm(''), ...{ placeholder: t('common.search') || 'Search' } }), children: _jsx(Box, { sx: {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        height: '100%',
                                        overflow: 'hidden'
                                    }, children: isInitialSuggestionsLoading ? (_jsx(Box, { sx: {
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            flex: 1
                                        }, children: _jsx(CircularProgress, {}) })) : suggestions && suggestions.length > 0 ? (_jsxs(_Fragment, { children: [_jsxs(Box, { ref: suggestionsContainerRef, sx: {
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1,
                                                    overflow: 'auto',
                                                    flex: 1,
                                                    pb: theme.spacing(2.5),
                                                    paddingRight: 0.5,
                                                    scrollbarWidth: 'thin',
                                                    '&::-webkit-scrollbar': {
                                                        width: '8px',
                                                    },
                                                    '&::-webkit-scrollbar-track': {
                                                        background: theme.palette.mode === 'dark'
                                                            ? 'rgba(255, 255, 255, 0.1)'
                                                            : 'rgba(0, 0, 0, 0.1)',
                                                        borderRadius: '4px',
                                                    },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        background: theme.palette.mode === 'dark'
                                                            ? 'rgba(255, 255, 255, 0.3)'
                                                            : 'rgba(0, 0, 0, 0.3)',
                                                        borderRadius: '4px',
                                                        '&:hover': {
                                                            background: theme.palette.mode === 'dark'
                                                                ? 'rgba(255, 255, 255, 0.5)'
                                                                : 'rgba(0, 0, 0, 0.5)',
                                                        },
                                                    },
                                                }, onScroll: handleSuggestionsScroll, children: [suggestions.map((suggestion) => (_jsx(SuggestionCards, { id: suggestion.id, name: suggestion.name, title: suggestion.title, email: suggestion.email, description: suggestion.description, avatar: suggestion.avatar, onAssignTicket: (id) => handleSuggestionAction(id, 'assign'), onTag: (id) => handleSuggestionAction(id, 'tag'), tags: suggestion.tags }, suggestion.id))), (isLoadingMore || (isLoading && suggestions.length > 0)) && (_jsx(Box, { sx: {
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            py: 2,
                                                        }, children: _jsx(CircularProgress, { size: 24 }) }))] }), hasMoreSuggestions && isAtBottom && (_jsx(Box, { sx: {
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    pt: 1,
                                                    pb: 1,
                                                }, children: _jsx(Button, { variant: "outlined", onClick: loadMoreSuggestions, disabled: isLoadingMore, size: "small", sx: {
                                                        minWidth: 120,
                                                        textTransform: 'none',
                                                        borderRadius: 2,
                                                        px: 2,
                                                        py: 0.5,
                                                    }, children: isLoadingMore
                                                        ? t('common.loading', { defaultValue: 'Loading...' })
                                                        : t('common.loadMore', { defaultValue: 'Load More' }) }) }))] })) : (_jsx(NoDataMessage, { text: t('common.noDataAvailable', {
                                            data: t('sponsorships.interestsTab.noSuggestions')
                                        }) })) }) }) }) })] }), _jsxs(Menu, { anchorEl: menuAnchor, open: Boolean(menuAnchor), onClose: closeMenu, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, children: [menuType === 'assign' && (_jsxs(_Fragment, { children: [_jsx(MenuItem, { onClick: () => handleAssignOption(menuRowId, 'Database'), children: t('sponsorships.tickets.assign.database') }), _jsx(MenuItem, { onClick: () => handleAssignOption(menuRowId, 'Manual'), children: t('sponsorships.tickets.assign.manual') })] })), menuType === 'send' && (_jsxs(_Fragment, { children: [_jsx(MenuItem, { onClick: () => handleSendOption(menuRowId, 'SendEmail'), disabled: currentMenuRow?.isTicketEmailSent, children: currentMenuRow?.isTicketEmailSent
                                    ? t('sponsorships.tickets.sent', { defaultValue: 'Sent' })
                                    : t('sponsorships.tickets.sendEmail') }), currentMenuRow && !currentMenuRow.isTicketEmailSent && (_jsx(MenuItem, { onClick: () => handleSendOption(menuRowId, 'Unassign'), children: t('sponsorships.tickets.unassign') }))] }))] }), _jsx(AssignDialog, { open: assignDialogOpen, onClose: handleAssignDialogClose, type: assignDialogType, ticketId: assignDialogRowId, onConfirm: handleAssignConfirm }), _jsx(ImportTicketDialog, { open: openImportExcelDialog, onClose: () => setOpenImportExcelDialog(false), isSmallScreen: isSmallScreen, eventId: eventId || '', onSuccess: () => {
                    getAllTickets();
                } }), _jsx(AssignTicketFromSuggestionDialog, { open: assignTicketFromSuggestionDialogOpen, onClose: () => {
                    setAssignTicketFromSuggestionDialogOpen(false);
                    setSelectedSuggestion(null);
                }, suggestion: selectedSuggestion })] }));
};
export default Tickets;
